import { useCallback, useEffect, useState } from 'react'

import type {
  FilterMode,
  FilterOption,
  FilterValue,
} from '../components/filter/Filter'
import {
  filterArraysEqualUnordered,
  handleFilterRowToggle,
} from '../utils/filterSelectionMath'

interface IUseFilterDraftArgs<V> {
  applied: FilterValue<V>
  mode: FilterMode
  isOpen: boolean
}

interface IUseFilterDraftResult<V> {
  draft: FilterValue<V>
  searchTerm: string
  setSearchTerm: (term: string) => void
  toggleRow: (option: FilterOption<V>) => void
  setDraft: (next: FilterValue<V>) => void
  clear: () => void
  reset: () => void
  isDirty: boolean
  isCleared: boolean
}

export const useFilterDraft = <V>({
  applied,
  mode,
  isOpen,
}: IUseFilterDraftArgs<V>): IUseFilterDraftResult<V> => {
  const [draft, setDraft] = useState<FilterValue<V>>(applied)
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    if (isOpen) {
      setDraft(applied)
      setSearchTerm('')
    }
  }, [isOpen, applied])

  const toggleRow = useCallback(
    (option: FilterOption<V>) => {
      setDraft((current) => handleFilterRowToggle(option, current, mode))
    },
    [mode]
  )

  const clear = useCallback(() => setDraft([]), [])
  const reset = useCallback(() => setDraft(applied), [applied])

  const isDirty = !filterArraysEqualUnordered(draft, applied)
  const isCleared = draft.length === 0

  return {
    draft,
    searchTerm,
    setSearchTerm,
    toggleRow,
    setDraft,
    clear,
    reset,
    isDirty,
    isCleared,
  }
}
